import { useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { LpFooter, LpShell } from '@/shared/components/LpShell';
import { Modal } from '@/shared/components/Modal';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { AuthModal } from '@/modules/auth/presentation/components/AuthModal';
import { HeaderAccount } from '@/modules/auth/presentation/components/HeaderAccount';
import { useMySubmission } from '@/modules/submissions/presentation/hooks/use-submissions';
import { track } from '@/lib/analytics';
import type { AnswerOption } from '@/modules/simulado/domain/entities/answer.entity';
import { toGabaritoPayload } from '../../domain/entities/gabarito.entity';
import { AnswerSheet } from '../components/AnswerSheet';
import {
  GABARITO_SUBMISSION_NOTE,
  useGabaritoSubmission,
} from '../components/SendGabaritoAction';
import { useFinishGabarito } from '../hooks/use-finish-gabarito';
import { useGabaritoDraft } from '../hooks/use-gabarito-draft';

/** Para onde o gabarito leva depois do envio: a correção, no painel. */
export const GABARITO_RESULT_PATH = '/minha-conta?aba=gabarito';

/**
 * O gabarito, depois da prova: **só a folha** — o `AnswerSheetStep` do
 * `enamed-feedback-lp`, sem a escolha de caderno (estas provas não têm) e sem
 * o modo "preencher na prova": quando a pessoa envia o gabarito a prova ainda
 * não foi publicada, então não há enunciado para mostrar. A aba Prova Oficial
 * do painel é que traz as questões, quando saírem.
 *
 * A pessoa marca o que respondeu e envia; o envio
 * pergunta se ela já tem conta e, tendo ou não feito o ensaio, ela entra ou se
 * cadastra e cai na correção.
 */
export function GabaritoPage() {
  const submission = useGabaritoSubmission();

  // Encerrado: a página não existe mais. Quem já enviou vê a correção no
  // painel, e o link antigo cai na home.
  if (submission === 'closed') return <Navigate to="/" replace />;
  if (submission === 'soon') return <GabaritoSoon />;
  return <GabaritoSheet />;
}

/**
 * Antes da prova: a página explica quando o envio abre, em vez de mostrar uma
 * folha que não dá para enviar. É o destino de quem guardou o link.
 */
function GabaritoSoon() {
  const { exam, theme, edition } = useTenant();

  return (
    <LpShell theme={theme} headerRight={<HeaderAccount />}>
      <title>{`Gabarito ${exam.shortName} ${edition.year}`}</title>
      <section className="fold">
        <div className="wrap wrap-narrow">
          <section className="card empty-state">
            <h3>
              Gabarito {exam.shortName} {edition.year}
            </h3>
            <p className="card-note">{GABARITO_SUBMISSION_NOTE.soon}</p>
            <div className="empty-state-actions">
              <Link className="cta cta-inline" to="/ensaio">
                Fazer o ensaio enquanto isso
              </Link>
            </div>
          </section>
        </div>
      </section>
      <LpFooter />
    </LpShell>
  );
}

function GabaritoSheet() {
  const { exam, theme, edition } = useTenant();
  const navigate = useNavigate();
  const { draft, setAnswer, clear } = useGabaritoDraft();
  const mySheet = useMySubmission('gabarito');
  const [isConfirming, setIsConfirming] = useState(false);
  const submitRef = useRef<HTMLButtonElement>(null);
  const { finish, authModalProps, isSubmitting, isError } = useFinishGabarito(
    () => {
      clear();
      navigate(GABARITO_RESULT_PATH);
    },
  );

  const answeredCount = Object.keys(draft.answers).length;
  const blank = edition.totalQuestions - answeredCount;

  return (
    <LpShell theme={theme} headerRight={<HeaderAccount />}>
      <title>{`Gabarito ${exam.shortName} ${edition.year}`}</title>

      <section className="fold">
        <div className="wrap">
          <h1 className="page-title">
            Gabarito {exam.shortName} {edition.year}
          </h1>
          <p className="page-lead">
            Marque o que você respondeu em cada uma das {edition.totalQuestions}{' '}
            questões. Pode deixar em branco o que não marcou na prova, e marcar
            de novo a mesma alternativa para desmarcar. O preenchimento fica
            salvo neste navegador.
            {/* Só no desktop: no celular não há teclado físico. */}
            <span className="kbd-hint">
              {' '}
              Use as teclas <kbd>A</kbd>–
              <kbd>{edition.optionCount === 5 ? 'E' : 'D'}</kbd> para marcar e{' '}
              <kbd>↑</kbd> <kbd>↓</kbd> para navegar.
            </span>
          </p>

          {mySheet.data && (
            <p className="notice page-body">
              Você já enviou um gabarito desta prova. Enviar de novo substitui o
              anterior.
            </p>
          )}

          <div className="page-body">
            <AnswerSheet
              totalQuestions={edition.totalQuestions}
              optionCount={edition.optionCount}
              answers={draft.answers}
              onAnswer={(question: number, answer: AnswerOption | null) => {
                // A primeira marcação é o começo de fato do preenchimento.
                if (answeredCount === 0 && answer) {
                  track({
                    event: 'gabarito_iniciado',
                    total: edition.totalQuestions,
                  });
                }
                setAnswer(question, answer);
              }}
              submitRef={submitRef}
            />
          </div>
        </div>
      </section>

      <div className="submitbar">
        <div className="wrap">
          <p className="submitbar-count">
            {answeredCount} de {edition.totalQuestions} marcadas
          </p>
          <button
            ref={submitRef}
            type="button"
            className="cta cta-inline"
            disabled={answeredCount === 0 || isSubmitting}
            onClick={() => setIsConfirming(true)}
          >
            {isSubmitting ? 'Enviando…' : 'Enviar gabarito'}
          </button>
        </div>
      </div>

      {isError && (
        <div className="wrap">
          <div className="alert stack-gap" role="alert">
            Não conseguimos enviar seu gabarito agora. Ele continua salvo. Tente
            de novo em instantes.
          </div>
        </div>
      )}

      <LpFooter />

      <Modal
        open={isConfirming}
        role="alertdialog"
        title="Enviar o gabarito?"
        onClose={() => setIsConfirming(false)}
        note={
          <>
            Você marcou {answeredCount} de {edition.totalQuestions} questões.{' '}
            {blank > 0 && (
              <strong>
                {blank === 1
                  ? '1 ficou em branco. '
                  : `${blank} ficaram em branco. `}
              </strong>
            )}
            Confira antes de enviar.
          </>
        }
      >
        <div className="modal-actions">
          <button
            type="button"
            className="ghost ghost-lg"
            onClick={() => setIsConfirming(false)}
          >
            Revisar
          </button>
          <button
            type="button"
            className="cta cta-inline cta-block"
            onClick={() => {
              setIsConfirming(false);
              track({
                event: 'gabarito_finalizado',
                respondidas: answeredCount,
                total: edition.totalQuestions,
              });
              finish(toGabaritoPayload(draft.answers));
            }}
          >
            Enviar
          </button>
        </div>
      </Modal>

      <AuthModal {...authModalProps} />
    </LpShell>
  );
}
